/**
 * @file        src/pages/faq/AddFaq.jsx
 * @description Create / edit an FAQ: brand, question, display order, status,
 *              answer. Reused for /faqs/new and /faqs/:id/edit. Clone of the
 *              testimonial form; brand comes from the tenant's registry
 *              (BrandSelect — never a hardcoded list) and is required.
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-08-04 Claude — Initial (FAQ Management, CR A2).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import BrandSelect from '../../components/BrandSelect';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';

const schema = z.object({
  brand: z.string().trim().min(1, 'Required'),
  question: z.string().trim().min(1, 'Required').max(500),
  answer: z.string().trim().min(1, 'Required').max(4000),
  display_order: z.coerce.number().int().min(0, 'Must be 0 or more'),
  is_active: z.boolean(),
});

const empty = { brand: '', question: '', answer: '', display_order: 0, is_active: true };

export default function AddFaq() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const { data: existing } = useQuery({
    queryKey: qk.faqs.one(id),
    queryFn: () => get(`/faqs/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing?.data) {
      const f = existing.data;
      setForm({
        brand: f.brand || '', question: f.question || '', answer: f.answer || '',
        display_order: f.display_order ?? 0, is_active: !!f.is_active,
      });
    }
  }, [existing]);

  const mut = useMutation({
    mutationFn: (b) => (isEdit ? patch(`/faqs/${id}`, b) : post('/faqs', b)),
    onSuccess: () => {
      toast.ok(isEdit ? 'FAQ updated' : 'FAQ added', 'Saved.');
      qc.invalidateQueries({ queryKey: ['faqs'] });
      navigate('/faqs');
    },
    onError: (err) => {
      const f = err.response?.data?.error?.fields;
      if (f) setErrors(f);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe = {};
      for (const i of parsed.error.issues) fe[i.path.join('.')] = i.message;
      return setErrors(fe);
    }
    setErrors({});
    mut.mutate(parsed.data);
  }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit FAQ' : 'Add FAQ'}
        subtitle="FAQ Management"
        actions={<button className="btn ghost" onClick={() => navigate('/faqs')}><Icon name="x" /> Cancel</button>}
      />
      <article className="panel glass">
        <form className="form-grid" onSubmit={onSubmit}>
          <Field label="Brand" required err={errors.brand}>
            <BrandSelect value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} invalid={!!errors.brand} />
          </Field>
          <Field label="Display Order" required err={errors.display_order}
            hint="Lower numbers show first on the FAQ page.">
            <input
              type="number"
              min={0}
              value={form.display_order}
              onChange={on('display_order')}
            />
          </Field>
          <Field label="Status">
            <Select
              value={form.is_active ? 'active' : 'inactive'}
              onChange={(v) => setForm({ ...form, is_active: v === 'active' })}
              options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
            />
          </Field>

          <Field label="Question" required err={errors.question} full>
            <input value={form.question} onChange={on('question')} placeholder="e.g. How do I cancel a booking?" maxLength={500} />
          </Field>

          {/* Capped in the UI: the API accepts 4,000 characters, and an answer
              anywhere near that long turns the accordion into a wall of text.
              An answer worth reading is a few sentences. */}
          <Field label="Answer" required err={errors.answer} full>
            <textarea
              rows={6}
              maxLength={4000}
              value={form.answer}
              onChange={on('answer')}
              placeholder="The answer shown when the question is expanded…"
            />
            <small className="muted">
              {(form.answer || '').length}/4000 characters · shown in the FAQ accordion
            </small>
          </Field>

          <div className="form-actions">
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Submit'}
            </button>
          </div>
        </form>
      </article>
    </>
  );
}

function Field({ label, required, err, hint, full, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {hint && !err ? <div className="field-hint muted">{hint}</div> : null}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
